import { type ReactElement, useMemo, useState } from "react";

import Button from "@atlaskit/button";
import LoadingButton from "@atlaskit/button/loading-button";
import Form, { type OnSubmitHandler } from "@atlaskit/form";

import {
    BottomNav,
    FormFooter,
    FormHeader,
    FormSection,
    type SiteNameDropdownOption,
    SiteNameField
} from "../../ui";

export type SiteNameFieldFormProps = {
    readonly value: string;
    readonly onSubmit: OnSubmitHandler<SiteNameFieldData>;
    readonly getSiteNameAvailability: (
        siteName: string
    ) => Promise<SiteNameAvailabilityStatus>;
    readonly sites: SiteNameDropdownOption[];
    readonly newSite: (value: boolean) => void;
    readonly renderPreview?: (value: string) => ReactElement;
    readonly delegationNav?: (value: boolean) => void;
    readonly provisionableProductCard?: ReactElement;
    readonly organizationCard?: ReactElement;
};

export type SiteNameAvailabilityStatus = "AVAILABLE" | "TAKEN" | "ERROR";

export type SiteNameFieldData = {
    siteName: string;
};

export const SiteNameFieldForm = ({
    getSiteNameAvailability,
    newSite,
    onSubmit,
    renderPreview,
    sites,
    value,
    delegationNav,
    provisionableProductCard,
    organizationCard
}: SiteNameFieldFormProps) => {
    const [isDisabled, setIsDisabled] = useState(false);

    const validateSiteName = useMemo(() => {
        return debounce(async (value: string | undefined) => {
            const re = /^[a-z0-9]+[a-z0-9-]+[a-z0-9]+$/;
            if (!value) {
                return "Site name is required.";
            } else if (value.length === 0) {
                return "Site name is required.";
            } else if (value.length < 3) {
                return "Site name must be at least three characters (numerals or lowercase letters only) and can’t start or end with a hyphen (-).";
            } else if (value.length > 50) {
                return "That's a bit too long — try something 50 characters or less.";
            } else if (!re.exec(value)) {
                return "Site name must be at least three characters (numerals or lowercase letters only) and can’t start or end with a hyphen (-).";
            } else {
                const availabilityStatus = await getSiteNameAvailability(value);
                if (availabilityStatus === "TAKEN") {
                    return "This site name is taken. Choose another name.";
                }
                if (availabilityStatus !== "AVAILABLE") {
                    return "An unknown error occurred while checking the site name availability.";
                }

                return "";
            }
        }, 500);
    }, [getSiteNameAvailability]);

    return (
        <Form<SiteNameFieldData> onSubmit={onSubmit}>
            {({ formProps, submitting, getState }) => {
                return (
                    <form
                        {...formProps}
                        data-testid="site-name-field-form"
                        // @ts-ignore
                        onChange={(s) => {
                            setIsDisabled(getState().hasValidationErrors);
                        }}
                    >
                        <FormHeader
                            title="Name your site"
                            description="Your site name is included in the URL, so be sure the name is something your teams will recognize. Don't worry! You can always change it later."
                        />
                        {!!provisionableProductCard && provisionableProductCard}
                        {!!organizationCard && organizationCard}
                        <FormSection>
                            <SiteNameField
                                fieldName="siteName"
                                fieldLabel="Name your site"
                                renderPreview={renderPreview}
                                value={value}
                                validate={validateSiteName}
                            />
                        </FormSection>
                        <FormFooter>
                            <LoadingButton
                                type="submit"
                                appearance="primary"
                                shouldFitContainer
                                isLoading={submitting}
                                isDisabled={submitting || isDisabled}
                            >
                                Create site
                            </LoadingButton>
                        </FormFooter>
                        {sites && sites.length > 0 && (
                            <BottomNav>
                                <Button
                                    appearance="link"
                                    onClick={() => newSite(false)}
                                >
                                    Use existing site
                                </Button>
                            </BottomNav>
                        )}
                        {delegationNav && (
                            <BottomNav>
                                <Button
                                    appearance="link"
                                    onClick={() => delegationNav(true)}
                                >
                                    Invite a teammate to name your site
                                </Button>
                            </BottomNav>
                        )}
                    </form>
                );
            }}
        </Form>
    );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DebouncedFunction<T extends any[], R> = (...args: T) => Promise<R>;

// this debounce function takes a function and a delay-duration and returns
// a promisified function that resolves to the returned or resolved value of
// the wrapped function when the debounce timeout is reached
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function debounce<T extends any[], R>(
    func: (...args: T) => R | Promise<R>,
    delay: number
): DebouncedFunction<T, R> {
    let timeout: ReturnType<typeof setTimeout>;
    let promise: Promise<R> | undefined;
    let resolve: (result: R) => void;
    let lastArgs: T;

    return (...args: T) => {
        lastArgs = args;

        clearTimeout(timeout);

        if (!promise) {
            promise = new Promise<R>((res) => {
                resolve = res;
            });
        }

        timeout = setTimeout(async () => {
            resolve(await func(...lastArgs));
            promise = undefined;
        }, delay);

        return promise;
    };
}
